import { useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Alert, Card, CardContent, Divider, IconButton, InputAdornment, Pagination, Snackbar, TextField } from '@mui/material';
import { safeElectronCall } from '../lib/util/electronHelpers';
import { checkDexieEnvironment } from '../lib/dexie/checks';

import AppBarDashboard from "./AppBarDashboard";
import NewAnalysisDialog from './NewAnalysisDialog';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { compareDates, removeAccents } from '../lib/util/util';

import CardAnalise from './CardAnalise';
import { useAuth } from '../context/AuthContext';
import SubscribeDialog from '../components/SubscribeDialog';
import GenericDialog from '../components/GenericDialog';
import { Search } from '@mui/icons-material';
import { setUserPropertiesAnalytics } from '../lib/firebase/analytics';

import Onboarding from './Onboarding';
import { createUserAnalysis, deleteUserAnalysis, getUserAnalysisList } from '../lib/dataService';
import DialogUpdate from '../components/dialogs/DialogUpdate';
import DialogSync from '../components/dialogs/DialogSync';
import DialogIndexedDB from '../components/dialogs/DialogIndexedDB';
import { syncDexieWithFirebase, syncAnalysisWithFirebase } from '../lib/dexie/syncFirebase';
import { onConnectionChange as firebaseConnectionChange } from '../lib/firebase/database';
import useOnline from '../hooks/useOnline';
import { logError } from '../lib/firebase/logs';
import { openPrimaryDownload } from '../lib/downloads';

const Dashboard = (props) => {

    const { user, permission, sendVerification, refreshUserData } = useAuth();
    const auth = getAuth();
    const navigate = useNavigate();
    const [analises, setAnalises] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [newAnalysisDialogOpen, setNewAnalysisDialogOpen] = useState(false);
    const [openSubscribeDialog, setOpenSubscribeDialog] = useState(false);
    const [openDialogValidation, setOpenDialogValidation] = useState(false);
    const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
    const electronVersionUrl = "https://gamevision.pro/electron-version.json";
    const [openOnboarding, setOpenOnboarding] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [openDialogIndexedDB, setOpenDialogIndexedDB] = useState(false);
    const [indexedDBMessage, setIndexedDBMessage] = useState('');
    const [openSyncDialog, setOpenSyncDialog] = useState(false);
    const isOnline = useOnline();
    const fileJsonRef = useRef(null);

    useEffect(() => {

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setCurrentUser(user);
            } else {
                navigate('/login');
            }
        });
        return () => { unsubscribe(); };

    }, [auth, navigate]);

    useEffect(() => {
        const fetchData = async () => {
            if (currentUser) {
                if (window.electron) {
                    const status = await checkDexieEnvironment();
                    if (!status.ok) {
                        setIndexedDBMessage(status.issues.map(i => i.message).join(' '));
                        setOpenDialogIndexedDB(true);
                        return;
                    }
                    let unsubscribe;
                    try {
                        unsubscribe = firebaseConnectionChange((snap) => {
                            if (snap.val() !== true) {
                                setOpenSyncDialog(false);
                            }
                        });
                        setOpenSyncDialog(true);
                        await syncDexieWithFirebase(currentUser.uid);
                        await refreshUserData();
                    } finally {
                        if (unsubscribe) unsubscribe();
                        setOpenSyncDialog(false);
                    }
                }
                const response = await getUserAnalysisList(currentUser.uid);
                if (response) {
                    const dados = Object.values(response);
                    const dadosOrdenados = dados.sort(compareDates);
                    setAnalises(dadosOrdenados);
                    setFilteredAnalises(dadosOrdenados);
                } else {
                    if (isOnline) setOpenOnboarding(true);
                }
                checkUpdates();
            }
        };

        fetchData();

    }, [currentUser]);

    const checkUpdates = async () => {
        if (window.electron) {
            try {
                const response = await fetch(electronVersionUrl, { cache: 'no-cache' });
                if (!response.ok) throw new Error('version fetch failed');
                const { version } = await response.json();
                const appVersion = await safeElectronCall('getAppVersion');
                if (version && appVersion !== version) {
                    setOpenUpdateDialog(true);
                }
            } catch {
                setOpenUpdateDialog(false);
            }
        }
    }

    useEffect(() => {
        const verifyIndexedDB = async () => {
            if (window.electron && typeof window.electron.checkIndexedDBDirectory === 'function') {
                try {
                    const result = await safeElectronCall('checkIndexedDBDirectory');
                    if (result) {
                        if (!result.writable) {
                            setIndexedDBMessage('Sem permissão de escrita no diretório de dados. Por favor, contate o suporte.');
                            setOpenDialogIndexedDB(true);
                            return;
                        }
                        if (!result.hasMinimumSpace) {
                            setIndexedDBMessage('Atenção! Você possui pouco espaço de armazenamento. Isso pode prejudicar o funcionamento do software. Por favor, libere espaço no seu disco.');
                            setOpenDialogIndexedDB(true);
                            return;
                        }
                        if (result.error) {
                            setIndexedDBMessage(result.error);
                            setOpenDialogIndexedDB(true);
                        }
                    }
                } catch (error) {
                    logError(error, { message: 'Erro ao verificar diretório do IndexedDB' });
                }
            }
            const status = await checkDexieEnvironment();
            if (!status.ok) {
                setIndexedDBMessage(status.issues.map(i => i.message).join(' '));
                setOpenDialogIndexedDB(true);
            }
        };
        verifyIndexedDB();
    }, []);


    const handleNewAnalysisDialogOpen = () => {

        setUserPropertiesAnalytics({
            user_type: permission ? "subscriber" : "registred",
            app_platform: window.electron ? "electron" : "web"
        });

        setNewAnalysisDialogOpen(true);

    }

    // SISTEMA DE BUSCA - INICIO ----------------------------------
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredAnalises, setFilteredAnalises] = useState(analises);

    const handleSearch = (term) => {
        const normalizedTerm = removeAccents(term.toLowerCase());
        if (normalizedTerm === "") {
            setFilteredAnalises(analises);
            return;
        }

        const filtered = analises.filter((analise) => {
            if (!analise) return false;
            if (!analise.title) return false;
            return removeAccents(analise.title.toLowerCase()).includes(normalizedTerm);
        });
        setFilteredAnalises(filtered);
    }
    const handleChangeSearchTerm = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        handleSearch(term);
        setPage(1);
    }
    useEffect(() => {
        handleSearch(searchTerm);
    }, [analises]);

    // SISTEMA DE PAGINAÇÃO - INÍCIO ----------------------------------
    const [page, setPage] = useState(1);
    //const [itemsPerPage, setItemsPerPage] = useState(14);
    const itemsPerPage = 14;

    const handleChangePage = (event, value) => {
        setPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top
    }
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedAnalises = filteredAnalises.slice(startIndex, endIndex);
    // SISTEMA DE PAGINAÇÃO - FIM ----------------------------------


    //header para que as imagens do storage funcionem - inicio ----------------------------------
    useEffect(() => {
        let embedderPolicyMeta = document.querySelector('meta[name="Cross-Origin-Embedder-Policy"]');
        let openerPolicyMeta = document.querySelector('meta[name="Cross-Origin-Opener-Policy"]');
        if (embedderPolicyMeta) {
            document.querySelector('meta[name="Cross-Origin-Embedder-Policy"]').removeAttribute('content');
            console.log("removeu");
        }
        if (openerPolicyMeta) {
            document.querySelector('meta[name="Cross-Origin-Opener-Policy"]').removeAttribute('content');
            console.log("removeu 2");
        }
    }, []);
    //header para que as imagens do storage funcionem - fim ----------------------------------

    const handleExcluirAnalise = async (analiseUid) => {
        const response = await deleteUserAnalysis(currentUser.uid, analiseUid);
        if (response) {
            setOpenSnackbar(true);
            setFilteredAnalises(filteredAnalises.filter((analise) => analise.uid !== analiseUid));
            if (window.electron) {
                syncAnalysisWithFirebase(currentUser.uid, analiseUid);
            }
        }
    }

    const handleJsonSelect = (event) => {
        const file = event.target.files[0];
        console.log("handleJsonSelect", file);

        if (!file || !file.name.endsWith(".json")) {
            alert("Selecione um arquivo .json válido.");
            return;
        }

        const reader = new FileReader();

        reader.onload = async (readerEvent) => {
            try {
                const jsonData = JSON.parse(readerEvent.target.result);
                console.log("jsonData", jsonData);

                if (jsonData && jsonData.title && jsonData.panel) {
                    const analysis = {
                        capa: "/images/capa-template.jpg",
                        data: new Date().toISOString(),
                        lastUpdate: new Date().toISOString(),
                        title: jsonData.title ? jsonData.title : '',
                        panel: jsonData.panel ? jsonData.panel : ''
                    }

                    const analiseUid = await createUserAnalysis(currentUser.uid, analysis, jsonData);

                    if (analiseUid) {
                        const path = jsonData.panel === "Edição de Vídeo (Motion)" ? "motion" : "analisar";
                        window.location.href = `/${path}/${analiseUid}`;
                    } else {
                        alert("Erro ao criar análise. Chame o Suporte.");
                    }

                } else {
                    alert("Arquivo JSON inválido. Verifique o formato do arquivo.");
                }
            } catch (error) {
                console.error("Erro ao processar JSON:", error);
                alert("Erro ao ler ou interpretar o arquivo JSON.");
            }
        };

        reader.readAsText(file);
    };


    const handleImportarAnalise = () => {
        fileJsonRef.current.click();
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', }}>
            <AppBarDashboard />

            {currentUser && (
                <Fade in={true}>
                    <Container component="main" maxWidth="false" sx={{ my: 8, p: 3 }}>

                        <Grid container spacing={3}>

                            <Grid item xs={6} sx={{ display: 'flex' }}>
                                <Typography variant="h5">Suas análises</Typography>
                            </Grid>

                            <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <TextField
                                    label="Busca"
                                    size="small"
                                    value={searchTerm}
                                    onChange={handleChangeSearchTerm}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    edge="end"
                                                    size="small"
                                                >
                                                    <Search fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            {user && !user.emailVerified && !permission && (
                                <Grid item xs={12}>
                                    <Alert
                                        severity="info"
                                        variant="filled"
                                        action={
                                            <Button color="inherit" size="small" onClick={() => { sendVerification(user) }}>
                                                Reenviar
                                            </Button>
                                        }
                                    >
                                        Você ainda não confirmou seu e-mail. Acesse o link enviado para: {user.email}
                                    </Alert>
                                </Grid>
                            )}

                            {!window.electron && (
                                <Grid item xs={12}>
                                    <Alert
                                        severity="info"
                                        variant="outlined"
                                        action={
                                            <Button variant="outlined" color="inherit" size="small" onClick={() => { openPrimaryDownload(); }}>
                                                download grátis
                                            </Button>
                                        }
                                    >

                                        Exporte vídeos 25x mais rápido com a versão Desktop.
                                    </Alert>
                                </Grid>
                            )}

                            <Grid item xs={12} sm={6} lg={4}>
                                <Card elevation={6} sx={{ minHeight: '300px', height: '100%', display: 'flex' }}>

                                    <CardContent sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>

                                        <Button
                                            data-testid="btn-criar-analise"
                                            disableElevation
                                            size="large"
                                            variant="contained"
                                            onClick={handleNewAnalysisDialogOpen}
                                        >
                                            Criar Nova Análise
                                        </Button>



                                        <Divider sx={{ my: 2 }}> ou </Divider>

                                        <Button
                                            disableElevation
                                            size="large"
                                            variant="outlined"
                                            onClick={handleImportarAnalise}
                                        >
                                            Importar Análise
                                        </Button>

                                    </CardContent>

                                </Card>
                            </Grid>

                            {paginatedAnalises && (
                                paginatedAnalises.map((analise, index) => (
                                    <Grid item xs={12} sm={6} lg={4} key={index}>
                                        <CardAnalise
                                            analise={analise}
                                            handleExcluirAnalise={handleExcluirAnalise}
                                        />
                                    </Grid>
                                ))
                            )}

                            {filteredAnalises.length > 0 && (
                                <Grid item xs={12} >
                                    <Pagination
                                        sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                                        count={Math.ceil(filteredAnalises.length / itemsPerPage)}
                                        page={page}
                                        onChange={handleChangePage}
                                    />
                                </Grid>
                            )}

                        </Grid>


                        <NewAnalysisDialog
                            userUid={currentUser.uid}
                            setNewAnalysisDialogOpen={setNewAnalysisDialogOpen}
                            newAnalysisDialogOpen={newAnalysisDialogOpen}
                            analises={analises}
                        />
                        <GenericDialog
                            openDialog={openDialogValidation}
                            setOpenDialog={setOpenDialogValidation}
                            title={"Confirme seu e-mail"}
                            message={"Para continuar você precisa confirmar o seu e-mail, acesse o link enviado para sua caixa de entrada."}
                        />

                        <DialogUpdate
                            openDialog={openUpdateDialog}
                            setOpenDialog={setOpenUpdateDialog}
                            title={"Atualização Disponível!"}
                            message={"Faça o download da nova versão para ter a melhor experiência. A nova atualização oferece correções de erros e novas funções."}
                        />


                        <Snackbar
                            open={openSnackbar}
                            autoHideDuration={4000}
                            onClose={() => { setOpenSnackbar(false); }}
                        >
                            <Alert
                                severity="success"
                                variant="filled"
                            >
                                Análise excluída com sucesso!
                            </Alert>
                        </Snackbar>

                    </Container>

                </Fade>

            )}
            <SubscribeDialog setOpenSubscribeDialog={setOpenSubscribeDialog} openSubscribeDialog={openSubscribeDialog} />

            <DialogIndexedDB
                openDialogIndexedDB={openDialogIndexedDB}
                setOpenDialogIndexedDB={setOpenDialogIndexedDB}
                message={indexedDBMessage}
            />

            {openOnboarding && isOnline && (
                <Onboarding
                    open={openOnboarding}
                    handleClose={() => { setOpenOnboarding(false); }}
                />
            )}

            <DialogSync open={openSyncDialog} />

            <input
                type="file"
                ref={fileJsonRef}
                style={{ display: 'none' }}
                onChange={handleJsonSelect}
                accept='application/json'
            />

        </Box>
    );

}

export default Dashboard;
